import { Injectable, computed, inject, signal } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, tap } from 'rxjs';
import { environment } from '../../../environments/environment';
import {
  FinancialStatus,
  NoticeSeverity,
  PlatformDashboard,
  PlatformPayment,
  PlatformTenant,
  PlatformTenantDetail,
  PlatformUser
} from '../models/super-admin.model';
import { AppModuleCode, PlanCode } from '../models/tenant.model';

const SESSION_KEY = 'super_admin_session';

interface SuperAdminSession {
  token: string;
  expiresAt: string;
}

// Portal do administrador geral (/super-admin). A sessão é separada das sessões do painel e das lojas.
@Injectable({ providedIn: 'root' })
export class SuperAdminService {
  private http = inject(HttpClient);
  readonly apiUrl = `${environment.apiUrl}/super-admin`;

  private readonly session = signal<SuperAdminSession | null>(readSession());
  readonly isAuthenticated = computed(() => {
    const session = this.session();
    return !!session && new Date(session.expiresAt) > new Date();
  });

  getToken(): string | null {
    return this.isAuthenticated() ? this.session()!.token : null;
  }

  login(password: string) {
    return this.http.post<{ access_token: string; expiresAt: string }>(`${this.apiUrl}/login`, { password }).pipe(
      tap(response => this.writeSession({ token: response.access_token, expiresAt: response.expiresAt }))
    );
  }

  logout() {
    this.writeSession(null);
  }

  // --- Relatórios e empresas ---

  dashboard(): Observable<PlatformDashboard> {
    return this.http.get<PlatformDashboard>(`${this.apiUrl}/dashboard`);
  }

  tenants(search = '', status: FinancialStatus | null = null): Observable<PlatformTenant[]> {
    let params = new HttpParams();
    if (search.trim()) params = params.set('search', search.trim());
    if (status) params = params.set('status', status);
    return this.http.get<PlatformTenant[]>(`${this.apiUrl}/tenants`, { params });
  }

  tenant(id: string): Observable<PlatformTenantDetail> {
    return this.http.get<PlatformTenantDetail>(`${this.apiUrl}/tenants/${id}`);
  }

  users(tenantId: string, search = '', role: 'ADMIN' | 'CUSTOMER' | null = null): Observable<PlatformUser[]> {
    let params = new HttpParams();
    if (search.trim()) params = params.set('search', search.trim());
    if (role) params = params.set('role', role);
    return this.http.get<PlatformUser[]>(`${this.apiUrl}/tenants/${tenantId}/users`, { params });
  }

  setUserActive(userId: string, isActive: boolean) {
    return this.http.patch<{ id: string; isActive: boolean }>(`${this.apiUrl}/users/${userId}`, { isActive });
  }

  setUsersActive(tenantId: string, isActive: boolean, role?: 'ADMIN' | 'CUSTOMER') {
    return this.http.patch<{ count: number }>(`${this.apiUrl}/tenants/${tenantId}/users`, { isActive, ...(role ? { role } : {}) });
  }

  // --- Ações sobre a assinatura (todas devolvem o detalhe atualizado da empresa) ---

  block(tenantId: string, reason: string) {
    return this.http.post<PlatformTenantDetail>(`${this.apiUrl}/tenants/${tenantId}/block`, { reason });
  }

  unblock(tenantId: string) {
    return this.http.post<PlatformTenantDetail>(`${this.apiUrl}/tenants/${tenantId}/unblock`, {});
  }

  updateSubscription(tenantId: string, data: { plan?: PlanCode; accessUntil?: string }) {
    return this.http.patch<PlatformTenantDetail>(`${this.apiUrl}/tenants/${tenantId}/subscription`, data);
  }

  registerPayment(tenantId: string, data: { plan: PlanCode; months: number; amount?: number; method?: string }) {
    return this.http.post<PlatformTenantDetail>(`${this.apiUrl}/tenants/${tenantId}/payments`, data);
  }

  cancelSubscription(tenantId: string) {
    return this.http.post<PlatformTenantDetail>(`${this.apiUrl}/tenants/${tenantId}/cancel`, {});
  }

  reactivateSubscription(tenantId: string) {
    return this.http.post<PlatformTenantDetail>(`${this.apiUrl}/tenants/${tenantId}/reactivate`, {});
  }

  updateModules(tenantId: string, data: { extraModules: AppModuleCode[]; disabledModules: AppModuleCode[] }) {
    return this.http.patch<PlatformTenantDetail>(`${this.apiUrl}/tenants/${tenantId}/modules`, data);
  }

  // --- Mensalidades e avisos ---

  payments(status: PlatformPayment['status'] | null = null): Observable<PlatformPayment[]> {
    const params = status ? new HttpParams().set('status', status) : undefined;
    return this.http.get<PlatformPayment[]>(`${this.apiUrl}/payments`, { params });
  }

  confirmPayment(paymentId: string) {
    return this.http.post<PlatformTenantDetail>(`${this.apiUrl}/payments/${paymentId}/confirm`, {});
  }

  // Sem tenantId, o aviso vai para todas as empresas
  sendNotice(data: { tenantId?: string; severity: NoticeSeverity; title: string; message: string }) {
    return this.http.post<{ count: number }>(`${this.apiUrl}/notices`, data);
  }

  private writeSession(session: SuperAdminSession | null) {
    try {
      if (session) localStorage.setItem(SESSION_KEY, JSON.stringify(session));
      else localStorage.removeItem(SESSION_KEY);
    } catch {
      // Sem armazenamento: a sessão vale só enquanto a página estiver aberta
    }
    this.session.set(session);
  }
}

function readSession(): SuperAdminSession | null {
  try {
    const stored = JSON.parse(localStorage.getItem(SESSION_KEY) ?? 'null');
    return stored?.token && stored?.expiresAt ? stored : null;
  } catch {
    return null;
  }
}
